import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Col, Button } from "react-bootstrap";
import { MCInput, MCSelect } from "../../MainComponents";
import { catalogByPart } from "../../../apis";
import { Checkbox, Tooltip, useTheme, CircularProgress } from "@mui/material";
import { OutlineQuestionCircle } from "../../Icon";

// Componente Selector con navegación hacia atrás para catálogos con hijos
const AreaBreadcrumbSelector = ({
  catalogue = "areaOfExpertise",
  isOwn = true,
  onChange,
  onLabelChange,
  lang,
  t, // función de traducción
}) => {
  // Historial de niveles: [{ key, label, path, options }, ...]
  const [history, setHistory] = useState([]);
  const [currentOptions, setCurrentOptions] = useState([]);
  const [currentSelection, setCurrentSelection] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isOther, setIsOther] = useState(false);
  const [customValue, setCustomValue] = useState("");

  // Detectar si es opción "Otro"
  const isOtherOption = (label) => {
    if (!label || typeof label !== 'string') return false;
    const lowerLabel = label.toLowerCase();
    return lowerLabel.includes('otro') || lowerLabel.includes('otros') || lowerLabel.includes('other');
  };

  // Cargar opciones de un path
  const loadOptions = useCallback(async (path = "/") => {
    setLoading(true);
    try {
      const response = await catalogByPart({
        is_own: isOwn,
        catalogue,
        path,
      });

      if (response?.data && Array.isArray(response.data)) {
        return response.data
          .filter(item => item.is_active)
          .map(item => ({
            label: item.locales?.[lang] || item.label,
            value: item.key,
            path: item.path
          }));
      }
      return [];
    } catch (error) {
      console.error("Error loading options:", error);
      return [];
    } finally {
      setLoading(false);
    }
  }, [catalogue, isOwn, lang]);

  // Inicializar - cargar primer nivel
  useEffect(() => {
    const init = async () => {
      const options = await loadOptions("/");
      setCurrentOptions(options);
    };
    init();
  }, [loadOptions]);

  // Construir label completo desde el historial
  const buildFullLabel = (historyArr, finalLabel) => {
    const labels = historyArr.map(h => h.label);
    if (finalLabel) labels.push(finalLabel);
    return labels.join(" > ");
  };

  // Sin selección en el nivel actual, el valor es el último departamento del historial
  const emitParent = (historyArr) => {
    const parent = historyArr[historyArr.length - 1];
    onChange(parent ? parent.key : null);
    onLabelChange(parent ? buildFullLabel(historyArr) : null);
  };

  // Manejar selección
  const handleSelect = async (selectedOption) => {
    if (!selectedOption) return;

    // Verificar si es "Otro"
    if (isOtherOption(selectedOption.label)) {
      setIsOther(true);
      setCustomValue("");
      setCurrentSelection({ ...selectedOption, isOther: true });
      emitParent(history);
      return;
    }

    setIsOther(false);
    setCustomValue("");

    // Cualquier nivel es una selección válida, tenga hijos o no
    onChange(selectedOption.value);
    onLabelChange(buildFullLabel(history, selectedOption.label));

    // Verificar si tiene hijos
    const children = selectedOption.path ? await loadOptions(selectedOption.path) : [];

    if (children.length > 0) {
      // Tiene hijos - guardar en historial y ofrecerlos como subdepartamento opcional
      setHistory(prev => [...prev, {
        key: selectedOption.value,
        label: selectedOption.label,
        path: selectedOption.path,
        options: currentOptions
      }]);
      setCurrentOptions(children);
      setCurrentSelection(null);
    } else {
      // Es el último nivel
      setCurrentSelection(selectedOption);
    }
  };

  // Volver al nivel anterior, dejando seleccionado el departamento de ese nivel
  const handleGoBack = () => {
    if (history.length === 0) return;

    const newHistory = [...history];
    const previousLevel = newHistory.pop();

    setHistory(newHistory);
    setCurrentOptions(previousLevel.options);
    setCurrentSelection({
      label: previousLevel.label,
      value: previousLevel.key,
      path: previousLevel.path,
    });
    setIsOther(false);
    setCustomValue("");
    onChange(previousLevel.key);
    onLabelChange(buildFullLabel(newHistory, previousLevel.label));
  };

  // Manejar input personalizado para "Otro"
  const handleCustomChange = (e) => {
    const val = e.target.value;
    setCustomValue(val);
    if (val.trim()) {
      onChange(val);
      onLabelChange(buildFullLabel(history, val));
    } else {
      emitParent(history);
    }
  };

  // Estilos inline forzados
  const backButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    minWidth: '32px',
    padding: '0',
    margin: '0',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#f5f5f5',
    color: '#333',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    flexShrink: 0,
  };

  return (
    <div style={{ width: '100%' }}>
      {/* Ruta de departamentos ya elegidos */}
      {history.length > 0 && (
        <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
          {buildFullLabel(history)}
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%' }}>
        {/* Botón volver - solo si hay historial */}
        {history.length > 0 && (
          <Tooltip title={`Volver a ${history.length > 1 ? history[history.length - 2].label : 'inicio'}`} arrow>
            <button
              type="button"
              onClick={handleGoBack}
              style={backButtonStyle}
            >
              ←
            </button>
          </Tooltip>
        )}

        {/* Input para "Otro" */}
        {isOther ? (
          <div style={{ flex: 1, position: 'relative' }}>
            <MCInput
              value={customValue}
              onChange={handleCustomChange}
              placeholder="Especifique..."
              autoFocus
              autoComplete="off"
              type="text"
            />
            <button
              type="button"
              style={{
                ...backButtonStyle,
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                width: '24px',
                height: '24px',
                minWidth: '24px',
                fontSize: '14px',
              }}
              onClick={() => {
                setIsOther(false);
                setCustomValue("");
                setCurrentSelection(null);
                emitParent(history);
              }}
              title="Volver al selector"
            >
              ←
            </button>
          </div>
        ) : (
          // Selector - siempre visible, con o sin selección
          <div style={{ flex: 1 }}>
            <MCSelect
              options={currentOptions}
              onChange={(e) => {
                if (!e) {
                  // Se limpió la selección
                  setCurrentSelection(null);
                  emitParent(history);
                  return;
                }
                const selected = currentOptions.find(opt => opt.value === e.value);
                handleSelect(selected);
              }}
              placeholder={loading ? "Cargando..." : t(history.length === 0 ? "formPreview.department_select" : "formPreview.department_select_sub")}
              value={currentSelection ? { label: currentSelection.label, value: currentSelection.value } : null}
              isDisabled={loading}
              isClearable
              menuPortalTarget={document.body}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// Componente que retorna un mensaje de error en caso de que el input sea invalido
const ReturnErrorMesages = ({ tryNext, valid }) => {
  return (
    <>
      {tryNext &&
        Array.isArray(valid) &&
        valid?.length > 0 &&
        valid.map((err, idx) => (
          <div key={idx} className="invalid-msg">
            {err} <br />
          </div>
        ))}
    </>
  );
};

// Input de Involucrados
export const FormInputInvolved = ({
  schema,
  value,
  valid,
  br,
  origin,
  tryNext,
  handleChange,
}) => {
  const [t] = useTranslation("forms");
  const theme = useTheme();

  const lang = localStorage.getItem("lang") || "es";

  const [nameInvolved, setNameInvolved] = useState("");
  const [emailInvolved, setEmailInvolved] = useState("");
  const [occupationInvolved, setOccupationInvolved] = useState("");
  const [tryToAdd, setTryToAdd] = useState(false);
  const [typeInvolved, setTypeInvolved] = useState(null);

  // Estados para checkbox "Desconozco"
  const [unknownName, setUnknownName] = useState(false);
  const [unknownEmail, setUnknownEmail] = useState(false);

  const [orderedInvolved, setOrderedInvolved] = useState([]);
  const [workstationOptions, setWorkstationOptions] = useState([]);
  // Estados para área con breadcrumb
  const [areaValue, setAreaValue] = useState(null);
  const [areaLabel, setAreaLabel] = useState(null);
  const [companyRelationshipOptions, setCompanyRelationshipOptions] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [companyRelationship, setCompanyRelationship] = useState(null);

  // Estados para controlar si se está en modo "Otro"
  const [isOtherCompanyRelationship, setIsOtherCompanyRelationship] = useState(false);
  const [isOtherOccupation, setIsOtherOccupation] = useState(false);

  // Estados para valores personalizados
  const [customCompanyRelationship, setCustomCompanyRelationship] = useState("");
  const [customOccupation, setCustomOccupation] = useState("");

  const optionsInvolved = [
    { label: t("formPreview.option_victim"), value: "victim" },
    { label: t("formPreview.option_witness"), value: "witness" },
    { label: t("formPreview.option_offender"), value: "offender" },
    // { label: t("formPreview.option_accomplice"), value: "accomplice" }, // Comentado temporalmente
  ];

  // Cargar las opciones de workstation desde el catálogo
  useEffect(() => {
    const loadWorkstationOptions = async () => {
      try {
        const response = await catalogByPart({
          is_own: true,
          catalogue: "workstation",
          path: "/",
        });

        if (response?.data && Array.isArray(response.data)) {
          const options = response.data
            .filter(item => item.is_active)
            .map(item => ({
              label: item.locales?.[lang] || item.label,
              value: item.key,
              path: item.path
            }));
          setWorkstationOptions(options);
        }
      } catch (error) {
        console.error("Error loading workstation options:", error);
      }
    };

    loadWorkstationOptions();
  }, [lang]);

  // Cargar las opciones de relación con la empresa desde el catálogo
  useEffect(() => {
    const loadCompanyRelationshipOptions = async () => {
      try {
        const response = await catalogByPart({
          is_own: true,
          catalogue: "companyRelationshipOffender",
          path: "/",
        });

        if (response?.data && Array.isArray(response.data)) {
          const options = response.data
            .filter(item => item.is_active)
            .map(item => ({
              label: item.locales?.[lang] || item.label,
              value: item.key,
              path: item.path
            }));
          setCompanyRelationshipOptions(options);
        }
      } catch (error) {
        console.error("Error loading company relationship options:", error);
      }
    };

    loadCompanyRelationshipOptions();
  }, [lang]);

  useEffect(() => {
    // Verificamos si value es null, undefined, o no es un array
    if (!value || !Array.isArray(value) || value?.length === 0) {
      setOrderedInvolved([]);
      return;
    }

    const tempVictims = [];
    const tempWitnesses = [];
    const tempOffenders = [];
    // const tempAccomplice = []; // Comentado temporalmente

    for (const [idx, involved] of value.entries()) {
      if (involved.type === "victim") {
        tempVictims.push({ idx, ...involved });
      }
      if (involved.type === "witness") {
        tempWitnesses.push({ idx, ...involved });
      }
      if (involved.type === "offender") {
        tempOffenders.push({ idx, ...involved });
      }
      // if (involved.type === "accomplice") { // Comentado temporalmente
      //   tempAccomplice.push({ idx, ...involved });
      // }
    }

    setOrderedInvolved([
      ...tempVictims,
      ...tempWitnesses,
      ...tempOffenders,
      // ...tempAccomplice, // Comentado temporalmente
    ]);
  }, [value]);

  // Función para detectar si un label contiene "Otro" u "Otros"
  const isOtherOption = (label) => {
    if (!label || typeof label !== 'string') return false;
    const lowerLabel = label.toLowerCase();
    return lowerLabel.includes('otro') || lowerLabel.includes('otros') || lowerLabel.includes('other');
  };

  // Limpiar ocupación y relación cuando cambia el tipo de involucrado
  useEffect(() => {
    if (typeInvolved) {
      setOccupationInvolved("");
      setCompanyRelationship(null);
      setIsOtherCompanyRelationship(false);
      setIsOtherOccupation(false);
      setCustomCompanyRelationship("");
      setCustomOccupation("");
      setAreaValue(null);
      setAreaLabel(null);
      setUnknownName(false);
      setUnknownEmail(false);
    }
  }, [typeInvolved]);

  const hdlAddInvolved = () => {
    setTryToAdd(true);
    if (!unknownName && nameInvolved.trim() === "") return;
    if (typeInvolved === null) return;

    const finalOccupation = isOtherOccupation ? customOccupation : occupationInvolved;
    const isOffender = typeInvolved === "offender";

    const involvedData = {
      type: typeInvolved,
      name: unknownName ? t("formPreview.unknown") : nameInvolved,
      email: unknownEmail ? t("formPreview.unknown") : emailInvolved,
    };

    // Solo agregar ocupación si tiene valor
    if (finalOccupation && finalOccupation.trim() !== "") {
      involvedData.occupation = finalOccupation;
    }

    // Si es offender, agregar el label de la ocupación del catálogo (si no es personalizado)
    if (isOffender && !isOtherOccupation) {
      const workstationObj = workstationOptions.find(item => item.value === occupationInvolved);
      if (workstationObj) {
        involvedData.occupationLabel = workstationObj.label;
      }
    } else if (isOtherOccupation) {
      involvedData.occupationLabel = customOccupation;
    }

    // Agregar la relación con la empresa para todos los tipos si se seleccionó
    const finalCompanyRelationship = isOtherCompanyRelationship ? customCompanyRelationship : companyRelationship;
    if (finalCompanyRelationship) {
      involvedData.companyRelationship = finalCompanyRelationship;
      if (!isOtherCompanyRelationship) {
        const relationshipObj = companyRelationshipOptions.find(item => item.value === companyRelationship);
        if (relationshipObj) {
          involvedData.companyRelationshipLabel = relationshipObj.label;
        }
      } else {
        involvedData.companyRelationshipLabel = customCompanyRelationship;
      }
    }

    // Agregar área solo si se seleccionó (usando breadcrumb)
    if (areaValue) {
      involvedData.area = areaValue;
      involvedData.areaLabel = areaLabel || areaValue;
    }

    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "add",
        value: involvedData,
      },
    });
    setNameInvolved("");
    setEmailInvolved("");
    setOccupationInvolved("");
    setAreaValue(null);
    setAreaLabel(null);
    setCompanyRelationship(null);
    setTypeInvolved(null);
    setIsOtherCompanyRelationship(false);
    setIsOtherOccupation(false);
    setCustomCompanyRelationship("");
    setCustomOccupation("");
    setUnknownName(false);
    setUnknownEmail(false);
    setTryToAdd(false);
    setShowAddForm(false);
  };

  const hdlDeleteInvolved = (idx) => {
    handleChange({
      target: {
        name: schema.key,
        type: "involved",
        action: "delete",
        idx,
      },
    });
    setNameInvolved("");
    setEmailInvolved("");
    setOccupationInvolved("");
    setAreaValue(null);
    setAreaLabel(null);
    setCompanyRelationship(null);
    setTypeInvolved(null);
    setUnknownName(false);
    setUnknownEmail(false);
  };

  const returnIsValid = (input) => {
    if (!tryToAdd) return true;
    if (input === "name" && !unknownName && nameInvolved.trim() === "") return false;
    if (input === "type" && typeInvolved === null) return false;
    return true;
  };

  return (
    <>
      <Col
        lg={schema.grid || 12}
        className={`preview-input-container form-input-involved`}
      >
        <label className={`${schema.required ? "label-required" : ""}`}>
          {schema.locales?.[lang] === undefined
            ? schema.label
            : schema.locales[lang]}
          :{schema.required ? " *" : ""}
        </label>

        {/* Agregar un involucrado */}
        <div className="form-input-involved__add-involved ">
          {!showAddForm ? (
            // Botón para iniciar el proceso de agregar involucrado
            <div className="w-100">
              <Button
                className="btn-send"
                style={{ color: 'white' }}
                onClick={() => setShowAddForm(true)}
              >
                {t("formPreview.involved_addInvolvedBtn")}
              </Button>
            </div>
          ) : (
            // Formulario para agregar involucrado
            <>
              {/* Primera fila: Tipo de involucrado */}
              <div className="form-input-involved__row">
                <div className="flex-grow-1">
                  <MCSelect
                    type="text"
                    name={`${origin}-involved-type`}
                    options={optionsInvolved}
                    isValid={returnIsValid("type")}
                    value={optionsInvolved.find(
                      (item) => item.value === typeInvolved
                    )}
                    onChange={(e) => setTypeInvolved(e.value)}
                    placeholder={t("formPreview.involved_type_placeholder")}
                  />
                </div>
              </div>

              {/* Segunda fila: Relación, Nombre, Correo */}
              {typeInvolved && (
                <div className="form-input-involved__row">
                  {/* Campo de relación con la empresa */}
                  <div className="flex-grow-1">
                    {!isOtherCompanyRelationship ? (
                      <MCSelect
                        type="text"
                        name={`${origin}-involved-company-relationship`}
                        options={companyRelationshipOptions}
                        value={companyRelationshipOptions.find((item) => item.value === companyRelationship)}
                        onChange={(e) => {
                          const selectedOption = companyRelationshipOptions.find(item => item.value === e.value);
                          if (selectedOption && isOtherOption(selectedOption.label)) {
                            setIsOtherCompanyRelationship(true);
                            setCompanyRelationship(null);
                          } else {
                            setCompanyRelationship(e.value);
                          }
                        }}
                        placeholder={t("formPreview.involved_companyRelationship_placeholder")}
                        isClearable
                      />
                    ) : (
                      <div style={{ position: 'relative' }}>
                        <MCInput
                          value={customCompanyRelationship}
                          onChange={(e) => setCustomCompanyRelationship(e.target.value)}
                          autoComplete="off"
                          type="text"
                          name={`${origin}-involved-custom-company-relationship`}
                          placeholder={t("formPreview.involved_companyRelationship_placeholder")}
                        />
                        <button
                          type="button"
                          className="back-to-select-btn"
                          onClick={() => {
                            setIsOtherCompanyRelationship(false);
                            setCustomCompanyRelationship("");
                          }}
                          title="Volver al selector"
                        >
                          ←
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Campo de nombre */}
                  <div className="flex-grow-1">
                    <div className="input-with-checkbox-inline">
                      <MCInput
                        value={unknownName ? t("formPreview.unknown") : nameInvolved}
                        onChange={(e) => setNameInvolved(e.target.value)}
                        autoComplete="off"
                        type="text"
                        isValid={returnIsValid("name")}
                        name={`${origin}-involved-name`}
                        placeholder={t("formPreview.involved_name_placeholder")}
                        disabled={unknownName}
                      />
                      <Tooltip
                        title={t("formPreview.unknown_tooltip")}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: theme.palette.primary.main,
                              fontSize: 12,
                              fontWeight: 500,
                              '& .MuiTooltip-arrow': {
                                color: theme.palette.primary.main,
                              },
                            },
                          },
                        }}
                      >
                        <div
                          className={`unknown-checkbox-btn ${unknownName ? 'active' : ''}`}
                          onClick={() => {
                            setUnknownName(!unknownName);
                            if (!unknownName) {
                              setNameInvolved("");
                            }
                          }}
                        >
                          <Checkbox
                            checked={unknownName}
                            size="small"
                            sx={{
                              padding: 0,
                              color: theme.palette.blueGray500.main,
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                          <OutlineQuestionCircle
                            size={16}
                            color={unknownName ? theme.palette.primary.main : theme.palette.blueGray500.main}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </div>

                  {/* Campo de correo */}
                  <div className="flex-grow-1">
                    <div className="input-with-checkbox-inline">
                      <MCInput
                        value={unknownEmail ? t("formPreview.unknown") : emailInvolved}
                        onChange={(e) => setEmailInvolved(e.target.value)}
                        autoComplete="off"
                        type="email"
                        name={`${origin}-involved-email`}
                        placeholder={t("formPreview.involved_email_placeholder")}
                        disabled={unknownEmail}
                      />
                      <Tooltip
                        title={t("formPreview.unknown_tooltip")}
                        arrow
                        placement="top"
                        slotProps={{
                          tooltip: {
                            sx: {
                              bgcolor: theme.palette.primary.main,
                              fontSize: 12,
                              fontWeight: 500,
                              '& .MuiTooltip-arrow': {
                                color: theme.palette.primary.main,
                              },
                            },
                          },
                        }}
                      >
                        <div
                          className={`unknown-checkbox-btn ${unknownEmail ? 'active' : ''}`}
                          onClick={() => {
                            setUnknownEmail(!unknownEmail);
                            if (!unknownEmail) {
                              setEmailInvolved("");
                            }
                          }}
                        >
                          <Checkbox
                            checked={unknownEmail}
                            size="small"
                            sx={{
                              padding: 0,
                              color: theme.palette.blueGray500.main,
                              '&.Mui-checked': {
                                color: theme.palette.primary.main,
                              },
                            }}
                          />
                          <OutlineQuestionCircle
                            size={16}
                            color={unknownEmail ? theme.palette.primary.main : theme.palette.blueGray500.main}
                          />
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                </div>
              )}

              {/* Tercera fila: Ocupación, Departamento, Botón */}
              {typeInvolved && (
                <div className="form-input-involved__row">
                  {/* Campo de ocupación: SELECT para offender, INPUT para victim/witness */}
                  <div className="flex-grow-1">
                    {typeInvolved === "offender" ? (
                      !isOtherOccupation ? (
                        <MCSelect
                          type="text"
                          name={`${origin}-involved-occupation`}
                          options={workstationOptions}
                          isValid={returnIsValid("occupation")}
                          value={workstationOptions.find((item) => item.value === occupationInvolved)}
                          onChange={(e) => {
                            const selectedOption = workstationOptions.find(item => item.value === e.value);
                            if (selectedOption && isOtherOption(selectedOption.label)) {
                              setIsOtherOccupation(true);
                              setOccupationInvolved("");
                            } else {
                              setOccupationInvolved(e.value);
                            }
                          }}
                          placeholder={t("formPreview.involved_occupation_placeholder")}
                        />
                      ) : (
                        <div style={{ position: 'relative' }}>
                          <MCInput
                            value={customOccupation}
                            onChange={(e) => setCustomOccupation(e.target.value)}
                            autoComplete="off"
                            type="text"
                            isValid={returnIsValid("occupation")}
                            name={`${origin}-involved-custom-occupation`}
                            placeholder={t("formPreview.involved_occupation_placeholder")}
                          />
                          <button
                            type="button"
                            className="back-to-select-btn"
                            onClick={() => {
                              setIsOtherOccupation(false);
                              setCustomOccupation("");
                            }}
                            title="Volver al selector"
                          >
                            ←
                          </button>
                        </div>
                      )
                    ) : (
                      <MCInput
                        value={occupationInvolved}
                        autoComplete="off"
                        type="text"
                        isValid={returnIsValid("occupation")}
                        name={`${origin}-involved-occupation`}
                        onChange={(e) => setOccupationInvolved(e.target.value)}
                        placeholder={t("formPreview.involved_occupation_placeholder")}
                      />
                    )}
                  </div>

                  <div className="flex-grow-1">
                    <AreaBreadcrumbSelector
                      key={`area-breadcrumb-${typeInvolved}`}
                      catalogue="rd102105f4af18ee"
                      isOwn={true}
                      onChange={setAreaValue}
                      onLabelChange={setAreaLabel}
                      lang={lang}
                      t={t}
                    />
                  </div>

                  <div className="flex-grow-1">
                    <Button
                      type="submit"
                      className="btn-form"
                      style={{ color: 'white' }}
                      onClick={() =>
                        hdlAddInvolved(nameInvolved, occupationInvolved, typeInvolved)
                      }
                    >
                      {t("formPreview.involved_addBtn")}
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="people-involved ">
          {orderedInvolved.length >= 1 ? (
            <>
              <div className="selectable-list">
                {orderedInvolved.map((item, idx) => {
                  const occupationDisplay = item.occupationLabel || item.occupation;
                  return (
                    <div key={idx} className={`selectable-item ${item.type}`}>
                      <strong>
                        {t(`formPreview.option_${item.type}`)}:&nbsp;
                      </strong>
                      {item.name}
                      {item.email && ` - ${item.email}`}
                      {occupationDisplay && ` (${occupationDisplay})`}
                      {item.companyRelationshipLabel && ` - ${item.companyRelationshipLabel}`}
                      {item.areaLabel && ` - ${item.areaLabel}`}{" "}
                      <button onClick={() => hdlDeleteInvolved(item.idx)}>
                        X
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          ) : (
            <div className="not-involved">
              {t("formPreview.not_involved_added")}
            </div>
          )}
        </div>

        <ReturnErrorMesages tryNext={tryNext} valid={valid} />
      </Col>
      {br && <div></div>}
    </>
  );
};
